/**
 * TrialBanner — sticky top bar shown during and after the free trial.
 *
 * Behaviours:
 *  - Trial active, days > 7  → subtle informational bar
 *  - Trial active, days ≤ 7  → amber warning with countdown
 *  - Trial active, days ≤ 2  → red urgent bar
 *  - Trial expired           → red full-width block with upgrade CTA
 *
 * Mount once in MainLayout (or wherever the app shell lives).
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, X, Zap, Clock } from 'lucide-react'
import { usePlan } from '../../hooks/usePlan'

// Days at which we show conversion nudge modals
const NUDGE_DAYS = [7, 2]

export function TrialBanner() {
  const { status, loading } = usePlan()
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(false)
  const [nudgeShown, setNudgeShown] = useState<number | null>(null)

  const tier = status?.plan_tier
  const daysLeft = status?.trial_days_remaining ?? null
  const expired = status?.trial_expired ?? false

  // Show conversion nudge modal at key days
  useEffect(() => {
    if (daysLeft === null) return
    for (const d of NUDGE_DAYS) {
      const key = `nudge_shown_${d}`
      if (daysLeft <= d && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1')
        setNudgeShown(d)
        break
      }
    }
  }, [daysLeft])

  if (loading) return null
  if (!tier || (tier !== 'trial' && tier !== 'trial_expired')) return null
  if (dismissed && !expired) return null

  // ── Expired state ────────────────────────────────────────────────────────
  if (expired) {
    return (
      <div className="w-full bg-red-600 text-white px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium">
        <AlertTriangle size={15} />
        <span>Your free trial has ended. Upgrade to keep working.</span>
        <button
          onClick={() => navigate('/billing')}
          className="ml-2 px-3 py-1 bg-white text-red-600 rounded-md text-xs font-bold hover:bg-red-50 transition-colors"
        >
          Upgrade now →
        </button>
      </div>
    )
  }

  // ── Active trial ─────────────────────────────────────────────────────────
  const urgent = daysLeft !== null && daysLeft <= 2
  const warning = daysLeft !== null && daysLeft <= 7

  const bg = urgent ? 'bg-red-500' : warning ? 'bg-amber-500' : 'bg-violet-600'
  const label = daysLeft === 0
    ? 'Your trial ends today.'
    : daysLeft === 1
    ? '1 day left in your free trial.'
    : daysLeft !== null
    ? `${daysLeft} days left in your free trial.`
    : 'You are on a free trial.'

  return (
    <>
      <div className={`w-full ${bg} text-white px-4 py-2 flex items-center justify-between gap-3 text-sm`}>
        <div className="flex items-center gap-2 font-medium">
          <Clock size={14} />
          <span>{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/billing')}
            className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-md text-xs font-bold transition-colors flex items-center gap-1"
          >
            <Zap size={11} />
            Upgrade
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            aria-label="Dismiss banner"
          >
            <X size={13} />
          </button>
        </div>
      </div>

      {/* Day 7 / Day 2 conversion nudge modal */}
      {nudgeShown !== null && (
        <NudgeModal
          daysLeft={nudgeShown}
          onClose={() => setNudgeShown(null)}
          onUpgrade={() => { setNudgeShown(null); navigate('/billing') }}
        />
      )}
    </>
  )
}

// ── Nudge modal ───────────────────────────────────────────────────────────────

function NudgeModal({
  daysLeft,
  onClose,
  onUpgrade,
}: {
  daysLeft: number
  onClose: () => void
  onUpgrade: () => void
}) {
  const urgent = daysLeft <= 2

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
          <X size={18} />
        </button>
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${urgent ? 'bg-red-100' : 'bg-violet-100'}`}>
          {urgent ? <AlertTriangle className="text-red-600" size={22} /> : <Zap className="text-violet-600" size={22} />}
        </div>
        <h2 className="text-lg font-bold text-foreground mb-2">
          {urgent ? `Only ${daysLeft} day${daysLeft === 1 ? '' : 's'} left!` : 'Half your trial is gone'}
        </h2>
        <p className="text-muted-foreground text-sm mb-5">
          {urgent
            ? "Your trial ends very soon. Upgrade now to keep all your projects, tasks, and data without interruption."
            : "You've been using Karyva for a week — your data, projects, and reports are all there. Upgrade to keep them."
          }
        </p>
        <div className="flex flex-col gap-2">
          <button
            onClick={onUpgrade}
            className="w-full py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2"
          >
            <Zap size={15} />
            See plans &amp; upgrade
          </button>
          <button onClick={onClose} className="w-full py-2 text-muted-foreground text-sm hover:text-foreground transition-colors">
            Remind me later
          </button>
        </div>
      </div>
    </div>
  )
}
